import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { NotificationModel } from '@/models/notification';
import NotificationService from '@/services/notificationService';
import { LoadingStateModel } from '@/models/loadingState';
import { useStudyStore } from './study';

const PAGE_SIZE = 25;

export const useNotificationStore = defineStore('notification', () => {
  const studyStore = useStudyStore();

  const notifications = ref<NotificationModel[]>([]);
  const unreadCount = ref<number>(0);
  const nextCursor = ref<string | null>(null);
  const scopeToStudy = ref<boolean>(true);

  const isLoading = reactive(new LoadingStateModel({ isLoading: false }));

  const hasMore = computed(() => nextCursor.value !== null);

  /**
   * The scope every request is made under, exposed so the view can reload when
   * it changes rather than only on mount.
   */
  const studyScope = computed(() =>
    scopeToStudy.value ? (studyStore.selectedStudyId ?? undefined) : undefined);

  /**
   * Bumped by every first-page load. An in-flight request compares against it
   * before writing: a study switch, or a second load, means the newer request
   * owns the store and these results describe a scope nobody is looking at.
   */
  let loadGeneration = 0;

  /** The scope the currently held page and count describe. */
  let loadedScope: string | undefined;

  /**
   * Load the first page, replacing whatever is held.
   *
   * The unread count is fetched alongside rather than derived from the loaded
   * page: the badge has to be right before anything is loaded, and it counts
   * rows beyond the first page.
   */
  const loadNotifications = async () => {
    const generation = ++loadGeneration;
    // Read once and reuse: the two requests run in sequence, and a study switch
    // between them would otherwise pair one scope's page with another's count.
    const scope = studyScope.value;

    // A scope change makes what is held wrong rather than merely stale, so it
    // goes before the fetch: if the request below fails, keeping the previous
    // study's rows and badge would show one study's notifications under
    // another. Only on a change, so a same-scope reload — the retry after a
    // failed `markRead`, or a revisit — does not blink an empty list.
    if (scope !== loadedScope) {
      loadedScope = scope;
      notifications.value = [];
      unreadCount.value = 0;
      nextCursor.value = null;
    }

    try {
      isLoading.startLoading();
      const page = await NotificationService.getNotifications({
        studyIdentifier: scope,
        limit: PAGE_SIZE
      });
      if (generation !== loadGeneration) return;
      notifications.value = page.notifications;
      nextCursor.value = page.nextCursor;

      const count = await NotificationService.getUnreadCount(scope);
      if (generation !== loadGeneration) return;
      unreadCount.value = count;
    } catch (error) {
      if (generation !== loadGeneration) return;
      isLoading.setError(error);
    } finally {
      // The load that superseded this one owns the loading flag now; clearing
      // it here would drop the spinner while that request is still running.
      if (generation === loadGeneration) isLoading.stopLoadingAndFinish();
    }
  };

  const loadMore = async () => {
    if (!nextCursor.value) return;

    const generation = loadGeneration;
    const cursor = nextCursor.value;

    try {
      isLoading.startLoading();
      const page = await NotificationService.getNotifications({
        studyIdentifier: studyScope.value,
        cursor,
        limit: PAGE_SIZE
      });
      // A reload while this page was in flight replaced the list underneath it,
      // so appending now would splice an older page — possibly another study's
      // — onto a freshly loaded one.
      if (generation !== loadGeneration) return;
      notifications.value = [...notifications.value, ...page.notifications];
      nextCursor.value = page.nextCursor;
    } catch (error) {
      if (generation !== loadGeneration) return;
      isLoading.setError(error);
    } finally {
      if (generation === loadGeneration) isLoading.stopLoadingAndFinish();
    }
  };

  /** Cheap enough to poll on an interval or call when the bell opens. */
  const refreshUnreadCount = async () => {
    const generation = loadGeneration;
    const scope = studyScope.value;

    try {
      const count = await NotificationService.getUnreadCount(scope);
      // The scope can change while this is in flight — a poll that started under
      // the previous study would otherwise stamp its count onto the new one's
      // badge. A first-page load started since fetches its own count, and that
      // one is the fresher of the two, so it wins as well.
      if (scope !== studyScope.value || generation !== loadGeneration) return;
      unreadCount.value = count;
    } catch {
      // A badge that lags is not worth surfacing an error banner for.
    }
  };

  /**
   * Optimistic: the row is marked locally first so the list does not flicker,
   * and the server call is what makes it stick. A failure re-reads rather than
   * rolling back by hand — the server is the authority on read state and the
   * page is one request away.
   */
  const markRead = async (notification: NotificationModel) => {
    if (!notification.isUnread) return;

    notification.readAt = new Date();
    unreadCount.value = Math.max(0, unreadCount.value - 1);

    try {
      await NotificationService.markRead(notification.id);
    } catch (error) {
      isLoading.setError(error);
      await loadNotifications();
    }
  };

  const markAllRead = async () => {
    const now = new Date();
    for (const notification of notifications.value) {
      if (notification.isUnread) notification.readAt = now;
    }
    unreadCount.value = 0;

    try {
      await NotificationService.markAllRead(studyScope.value);
    } catch (error) {
      isLoading.setError(error);
      await loadNotifications();
    }
  };

  /**
   * A push that arrived while the app is open.
   *
   * Prepended so the centre reflects it immediately instead of waiting for the
   * next fetch. It carries no row id, so it is de-duplicated on the id the API
   * stamps into the payload when there is one; a later refresh replaces it with
   * the persisted row either way.
   */
  const prependFromPush = (notification: NotificationModel) => {
    // A push for another study belongs in that study's list, not this one's —
    // prepending it here would show and count it under the selected study until
    // the next refresh corrected it. A push that names no study is kept: it is
    // not scoped to anything, so there is nothing for it to contradict.
    const scope = studyScope.value;
    if (scope && notification.studyId && notification.studyId !== scope) return;

    if (notifications.value.some(existing => existing.id === notification.id)) {
      return;
    }
    notifications.value = [notification, ...notifications.value];
    unreadCount.value += 1;
  };

  return {
    notifications,
    unreadCount,
    hasMore,
    scopeToStudy,
    studyScope,
    isLoading,
    loadNotifications,
    loadMore,
    refreshUnreadCount,
    markRead,
    markAllRead,
    prependFromPush
  };
});
